import { useEffect, useState } from "react";
import * as THREE from "three";
import { eclipticToGalactic, GALACTIC_BASIS_ECL } from "@solar/ephemeris";
import { assetByid, textureUrl } from "./assets";

const loader = new THREE.TextureLoader();
const cache = new Map<string, Promise<THREE.Texture | null>>();

/** 加载一张真实贴图；失败返回 null（调用方回退到程序生成贴图，需求 A11）。 */
export function loadTexture(id: string, opts: { srgb?: boolean; invert?: boolean } = {}): Promise<THREE.Texture | null> {
  const key = `${id}|${opts.srgb ?? true}|${opts.invert ?? false}`;
  let p = cache.get(key);
  if (!p) {
    const a = assetByid(id);
    p = !a ? Promise.resolve(null) : new Promise((resolve) => {
      loader.load(textureUrl(a.file), (t) => {
        t.colorSpace = opts.srgb === false ? THREE.NoColorSpace : THREE.SRGBColorSpace;
        t.wrapS = THREE.RepeatWrapping; t.anisotropy = 8;
        if (opts.invert) resolve(invert(t)); else resolve(t);
      }, undefined, () => { console.warn(`[textures] failed to load ${a.file}; falling back to procedural`); resolve(null); });
    });
    cache.set(key, p);
  }
  return p;
}

/** 反相：地球高光图（海洋白）→ 粗糙度图（海洋光滑）。 */
function invert(t: THREE.Texture): THREE.Texture {
  const img = t.image as HTMLImageElement, c = document.createElement("canvas");
  c.width = img.width; c.height = img.height;
  const g = c.getContext("2d")!; g.drawImage(img, 0, 0);
  const d = g.getImageData(0, 0, c.width, c.height);
  for (let i = 0; i < d.data.length; i += 4) { d.data[i] = 255 - d.data[i]!; d.data[i + 1] = 255 - d.data[i + 1]!; d.data[i + 2] = 255 - d.data[i + 2]!; }
  g.putImageData(d, 0, 0);
  const out = new THREE.CanvasTexture(c);
  out.colorSpace = THREE.NoColorSpace; out.wrapS = THREE.RepeatWrapping; out.anisotropy = 8;
  return out;
}

export interface RealMaps { map?: THREE.Texture; night?: THREE.Texture; clouds?: THREE.Texture; rough?: THREE.Texture }
const MAP_IDS: Record<string, { map?: string; night?: string; clouds?: string; spec?: string }> = {
  mercury: { map: "mercury" }, venus: { map: "venus" }, earth: { map: "earth", night: "earth-night", clouds: "earth-clouds", spec: "earth-spec" },
  moon: { map: "moon" }, mars: { map: "mars" }, jupiter: { map: "jupiter" }, saturn: { map: "saturn" }, uranus: { map: "uranus" }, neptune: { map: "neptune" },
};

/** 某天体的真实贴图（加载完成前返回 null → 先用程序贴图，加载完成后无缝替换）。 */
export function useRealMaps(id: string): RealMaps | null {
  const [maps, setMaps] = useState<RealMaps | null>(null);
  useEffect(() => {
    const spec = MAP_IDS[id];
    if (!spec) { setMaps(null); return; }
    let alive = true;
    (async () => {
      const [map, night, clouds, rough] = await Promise.all([
        spec.map ? loadTexture(spec.map) : null, spec.night ? loadTexture(spec.night) : null,
        spec.clouds ? loadTexture(spec.clouds, { srgb: false }) : null, spec.spec ? loadTexture(spec.spec, { srgb: false, invert: true }) : null,
      ]);
      if (alive) setMaps(map ? { map, night: night ?? undefined, clouds: clouds ?? undefined, rough: rough ?? undefined } : null);
    })();
    return () => { alive = false; };
  }, [id]);
  return maps;
}

/**
 * 银河背景：Solar System Scope 的贴图是银道坐标（银心在中心，银经向左增加，纬度轴上下颠倒——
 * 用大、小麦哲伦云的位置核对过：它们在图中偏上，而实际银纬为负）。这里按每个输出像素的黄道方向反查银道坐标取样，
 * 得到与场景坐标一致的黄道等距柱状贴图。
 */
export async function loadMilkyWay(size: { w: number; h: number }): Promise<THREE.Texture | null> {
  const a = assetByid("milkyway");
  if (!a) return null;
  const img = await new Promise<HTMLImageElement | null>((res) => { const i = new Image(); i.onload = () => res(i); i.onerror = () => res(null); i.src = textureUrl(a.file); });
  if (!img) return null;
  const sw = 4096, sh = 2048, src = document.createElement("canvas");
  src.width = sw; src.height = sh;
  const sg = src.getContext("2d")!; sg.drawImage(img, 0, 0, sw, sh);
  const S = sg.getImageData(0, 0, sw, sh).data;
  const out = document.createElement("canvas"); out.width = size.w; out.height = size.h;
  const og = out.getContext("2d")!, O = og.createImageData(size.w, size.h), D = O.data;
  const { x: gx, y: gy, z: gz } = GALACTIC_BASIS_ECL;
  for (let j = 0; j < size.h; j++) {
    const lat = Math.PI / 2 - ((j + 0.5) / size.h) * Math.PI, cl = Math.cos(lat), sl = Math.sin(lat);
    for (let i = 0; i < size.w; i++) {
      const lon = ((i + 0.5) / size.w) * Math.PI * 2 - Math.PI;
      const x = cl * Math.cos(lon), y = cl * Math.sin(lon), z = sl;
      const X = x * gx.x + y * gx.y + z * gx.z, Y = x * gy.x + y * gy.y + z * gy.z, Z = x * gz.x + y * gz.y + z * gz.z;
      const l = Math.atan2(Y, X), b = Math.asin(Math.max(-1, Math.min(1, Z)));       // l ∈ (−π, π]
      const u = 0.5 - l / (2 * Math.PI), v = 0.5 + b / Math.PI;                       // 向左增加；上下颠倒
      const px = Math.min(sw - 1, Math.max(0, Math.floor(u * sw))), py = Math.min(sh - 1, Math.max(0, Math.floor(v * sh)));
      const s = (py * sw + px) * 4, o = (j * size.w + i) * 4;
      D[o] = S[s]!; D[o + 1] = S[s + 1]!; D[o + 2] = S[s + 2]!; D[o + 3] = 255;
    }
  }
  og.putImageData(O, 0, 0);
  const t = new THREE.CanvasTexture(out);
  t.colorSpace = THREE.SRGBColorSpace; t.wrapS = THREE.RepeatWrapping; t.anisotropy = 4;
  return t;
}
void eclipticToGalactic;
